import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import './ReviewWrite.css';

function ReviewWrite() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const orderId = searchParams.get('orderId');
    
    const [order, setOrder] = useState(null);
    const [selectedItem, setSelectedItem] = useState(null);
    const [rating, setRating] = useState(5);
    const [content, setContent] = useState('');
    const [image, setImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (orderId) {
            fetchOrder();
        }
    }, [orderId]);

    const fetchOrder = async () => {
        try {
            const token = localStorage.getItem('access_token');
            const response = await axios.get(
                `https://api.weartoday.kr/api/orders/${orderId}/`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            setOrder(response.data);
            if (response.data.items?.length > 0) {
                setSelectedItem(response.data.items[0]);
            }
        } catch (error) {
            console.error('주문 조회 실패:', error);
        } finally {
            setLoading(false);
        }
    };

    const getImageUrl = (url) => {
        if (!url) return 'https://via.placeholder.com/80';
        if (url.startsWith('/media')) {
            return `https://api.weartoday.kr${url}`;
        }
        return url;
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setImage(file);
            setImagePreview(URL.createObjectURL(file));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!selectedItem) {
            alert('리뷰할 상품을 선택해주세요.');
            return;
        }
        
        if (!content.trim()) {
            alert('리뷰 내용을 입력해주세요.');
            return;
        }

        setSubmitting(true);
        
        try {
            const token = localStorage.getItem('access_token');
            const formData = new FormData();
            formData.append('product', selectedItem.product);
            formData.append('rating', rating);
            formData.append('content', content);
            if (image) {
                formData.append('image', image);
            }

            // ✅ URL 수정: /api/reviews/ → /api/products/reviews/
            await axios.post(
                'https://api.weartoday.kr/api/products/reviews/',
                formData,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );

            alert('리뷰가 등록되었습니다!');
            navigate('/orders');
        } catch (error) {
            console.error('리뷰 등록 실패:', error);
            if (error.response?.status === 400) {
                alert('이미 리뷰를 작성한 상품입니다.');
            } else {
                alert('리뷰 등록에 실패했습니다.');
            }
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="review-write-page">
                <div className="loading">⏳ 로딩 중...</div>
            </div>
        );
    }

    if (!order) {
        return (
            <div className="review-write-page">
                <div className="error">주문 정보를 찾을 수 없습니다.</div>
                <button onClick={() => navigate('/orders')}>목록으로</button>
            </div>
        );
    }

    return (
        <div className="review-write-page">
            <div className="review-write-container">
                <div className="review-header">
                    <button className="btn-back" onClick={() => navigate(-1)}>← 뒤로</button>
                    <h1>리뷰 작성</h1>
                </div>

                {/* 상품 선택 */}
                <div className="section">
                    <h2>📦 리뷰할 상품 선택</h2>
                    <div className="items-select">
                        {order.items?.map((item, idx) => (
                            <div 
                                key={idx} 
                                className={`item-option ${selectedItem?.product === item.product ? 'selected' : ''}`}
                                onClick={() => setSelectedItem(item)}
                            >
                                <img src={getImageUrl(item.product_image)} alt={item.product_name} />
                                <div className="item-info">
                                    <h3>{item.product_name}</h3>
                                    {item.option_name && <p>옵션: {item.option_name}</p>}
                                    {item.size && <p>사이즈: {item.size}</p>}
                                </div>
                                <div className="check-mark">
                                    {selectedItem?.product === item.product && '✓'}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* 별점 */}
                <div className="section">
                    <h2>⭐ 별점</h2>
                    <div className="rating-select">
                        {[1, 2, 3, 4, 5].map((star) => (
                            <span
                                key={star}
                                className={`star ${star <= rating ? 'active' : ''}`}
                                onClick={() => setRating(star)}
                            >
                                ★
                            </span>
                        ))}
                        <span className="rating-text">{rating}점</span>
                    </div>
                </div>

                {/* 리뷰 내용 */}
                <div className="section">
                    <h2>✍️ 리뷰 내용</h2>
                    <textarea
                        value={content}
                        onChange={(e) => setContent(e.target.value)}
                        placeholder="상품에 대한 솔직한 리뷰를 작성해주세요. (최소 10자)"
                        rows={6}
                    />
                    <div className="char-count">{content.length}자</div>
                </div>

                {/* 사진 첨부 */}
                <div className="section">
                    <h2>📷 사진 첨부 (선택)</h2>
                    <div className="image-upload">
                        <input
                            type="file"
                            id="review-image"
                            accept="image/*"
                            onChange={handleImageChange}
                            hidden
                        />
                        <label htmlFor="review-image" className="upload-btn">
                            {imagePreview ? (
                                <img src={imagePreview} alt="미리보기" />
                            ) : (
                                <span>+ 사진 추가</span>
                            )}
                        </label>
                        {imagePreview && (
                            <button 
                                className="remove-image"
                                onClick={() => {
                                    setImage(null);
                                    setImagePreview(null);
                                }}
                            >
                                ✕ 삭제
                            </button>
                        )}
                    </div>
                </div>

                {/* 제출 버튼 */}
                <div className="submit-section">
                    <button 
                        className="btn-submit"
                        onClick={handleSubmit}
                        disabled={submitting || !content.trim() || content.length < 10}
                    >
                        {submitting ? '등록 중...' : '리뷰 등록하기'}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default ReviewWrite;